<script setup lang="ts">
import ElLogo from '../logo-themable/Logo.component.vue'
import { config } from '@/config'
import { useLocalization } from '@/pages-hooks'
import CouponPromotionComponent from './CouponPromotion.component.vue'

interface IHeroData {
  brandMotto: string
  title: string
  subtitle: string
  shortSummary?: string
  summary?: string
}
interface TProps {
  lcid: string
  currentScrollY: number
  heroData: IHeroData
}

const props = defineProps<TProps>()

// const featureAppScreenPngSrc = `/feature-app-screenshot-${props.lcid}.png`
// const featureAppScreenWebpSrc = `/feature-app-screenshot-${props.lcid}.webp`

// retrieve page data passed down from layouts:
//const heroData: IHeroData = <any>useAttrs().hero

const thresholdY = 10
const { translate, getLocalizedPathPrefix } = useLocalization()

const logoCssClass = computed(() => {
  const result = ['hero-logo shrink-0 transition-all duration-300']
  result.push(`opacity-${props.currentScrollY > thresholdY ? 0 : 100}`)
  result.push(`max-w-${props.currentScrollY > thresholdY ? 60 : 80}`)
  return result.join(' ').trim()
})
</script>

<template>
  <div id="hero" class="relative isolate overflow-hidden">
    <svg
      class="absolute inset-0 -z-10 h-full w-full stroke-slate-400/10 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
      aria-hidden="true"
    >
      <defs>
        <pattern id="svg_fill_pattern_1" width="20" height="20" x="50%" y="-1" patternUnits="userSpaceOnUse">
          <path d="M.5 200V.5H200" fill="none" />
        </pattern>
      </defs>
      <!-- <svg x="75%" y="25%" class="overflow-visible fill-primary opacity-30">
        <path
          d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
          stroke-width="0"
        />
      </svg> -->
      <rect width="100%" height="100%" stroke-width="0" fill="url(#svg_fill_pattern_1)" />
    </svg>

    <div
      class="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]"
      aria-hidden="true"
    >
      <div
        class="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-primary to-secondary opacity-30"
        style="
          clip-path: polygon(
            73.6% 51.7%,
            91.7% 11.8%,
            100% 46.4%,
            97.4% 82.2%,
            92.5% 84.9%,
            75.7% 64%,
            55.3% 47.5%,
            46.5% 49.4%,
            45% 62.9%,
            50.3% 87.2%,
            21.3% 64.1%,
            0.1% 100%,
            5.4% 51.1%,
            21.4% 63.9%,
            58.9% 0.2%,
            73.6% 51.7%
          );
        "
      ></div>
    </div>
    <div class="mx-auto max-w-7xl px-6 pb-24 pt-8 sm:pb-32 lg:flex lg:px-8 lg:py-40">
      <div class="mx-auto max-w-2xl flex-shrink-0 lg:mx-0 lg:max-w-xl lg:pt-2 pr-4">
        <ElLogo :cssClass="logoCssClass" :brandConfig="config.brand" />

        <!-- <div class="mt-24 sm:mt-32 lg:mt-16">
          <a href="#" class="inline-flex space-x-6">
            <span class="rounded-full bg-indigo-500/10 px-3 py-1 text-sm font-semibold leading-6 text-indigo-400 ring-1 ring-inset ring-indigo-500/20">What's new</span>
            <span class="inline-flex items-center space-x-2 text-sm font-medium leading-6">
              <span>Just shipped v1.0</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" />
              </svg>
            </span>
          </a>
        </div> -->

        <h1 class="text-primary mt-20 text-4xl font-bold tracking-tight sm:text-6xl">
          {{ heroData?.brandMotto }}
        </h1>

        <h2 class="mt-6 text-lg leading-8 opacity-80 py-4">
          {{ heroData?.subtitle }}
        </h2>

        <p v-if="heroData?.shortSummary">
          {{ heroData?.shortSummary }}
        </p>
        <CouponPromotionComponent />
        <p class="hidden">
          {{ heroData?.summary }}
        </p>

        <div class="mt-10 flex items-center gap-x-6">
          <NuxtLink
            :to="`${getLocalizedPathPrefix(lcid)}/releases#top`"
            tabindex="0"
            class="bg-primary content-primary hover:bg-primary rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {{ translate('navigation.releases') }}
          </NuxtLink>

          <a href="#securityfirst" class="text-primary hover:text-primary text-sm font-semibold leading-6">
            {{ translate('navigation.learnMore') }} <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>

      <div class="group mt-16 flex flex-col sm:mt-24 lg:mr-0 lg:mt-0">
        <span
          class="h-8 text-base transition-color duration-150 text-secondary opacity-100 sm:opacity-0 sm:invisible group-hover:visible group-hover:opacity-100 hover:text-primary"
          ><a
            rel="noopener"
            href="https://react.tenorlab.com"
            target="_blank"
            title="Open the TenoraLab React Dashboard Demo"
            >Click here for a live demo</a
          >
        </span>
        <picture>
          <source srcset="/feature-app-screenshot.webp" type="image/webp" />
          <source srcset="/feature-app-screenshot.png" type="image/png" />
          <img
            src="/feature-app-screenshot.webp"
            alt="TenoraLab React Dashboard Demo screenshot"
            class="w-[46rem] max-w-none rounded-xl shadow-xl sm:w-[55rem] md:-ml-4 lg:-ml-0 transition-color duration-150 border-2 border-primary border-opacity-20"
            width="2720"
            heigh="1924"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
        </picture>
      </div>
    </div>
  </div>
</template>
